import {
  MediaType,
  PickerMode,
  useCurrentAccountAvatar,
} from '@gripp/shared-logic';
import { useEffect, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import { ImageComponent as CropCircleImage } from '../crop-circle/Components/Image/Image';
import { ImageData, ImagePickerComponent, SizePrefix } from '../media/types';
import { useTypedMediaItem } from '../media/useTypedMediaItem';

const AVATAR_SIZE = 88;

export type AvatarUploadProps = {
  /** Base media URL used to resolve the saved avatar thumbnail. */
  mediaUrl: string;
  /** Platform media picker (web `MediaPicker` / native `ImagePicker`). */
  imagePicker: ImagePickerComponent;
  /** Square edge length of the avatar. @default 88 */
  size?: number;
  /** Fires `true` while an image is uploading, `false` when idle. */
  onUploadingChange?: (isUploading: boolean) => void;
  /**
   * Fires with the uploaded media id once a pick finishes uploading, or `null`
   * when a freshly-picked image is discarded. The parent commits this id with
   * the rest of the profile on save.
   */
  onStagedMediaChange?: (mediaId: string | null) => void;
};

/**
 * Crop Circle profile-picture uploader. Mirrors the asset image-upload flow:
 * picking an image uploads it immediately (gating save while in flight), the
 * uploaded image stages into the form, and the freshly-picked image can be
 * discarded with the close (x) control before saving. The selected id is
 * committed by the parent form's save — it is not auto-committed here.
 */
export const AvatarUpload = ({
  mediaUrl,
  imagePicker,
  size = AVATAR_SIZE,
  onUploadingChange,
  onStagedMediaChange,
}: AvatarUploadProps) => {
  const ImagePicker = imagePicker;
  const { currentAvatar } = useCurrentAccountAvatar();
  const {
    typedMediaItem: staged,
    handleSet,
    handleUploadProgress,
    handleRefreshMediaItem,
  } = useTypedMediaItem<ImageData>(undefined);
  const openPickerRef = useRef<(() => void) | null>(null);

  const isUploading = !!staged?.isUploading;

  // Surface the uploaded id to the parent once the in-flight upload settles.
  useEffect(() => {
    if (staged?.id && staged.isUploading === false) {
      onStagedMediaChange?.(staged.id);
    }
  }, [staged?.id, staged?.isUploading, onStagedMediaChange]);

  const stagedSource =
    staged?.url ?? staged?.urlsBySize?.[SizePrefix.extraSmall];
  const existingSource =
    currentAvatar?.status !== 'PENDING' && currentAvatar?.id
      ? `${mediaUrl}/images/${currentAvatar.id}/xs.${currentAvatar.extension}`
      : undefined;
  const displaySource = stagedSource ?? existingSource;

  const openPicker = () => openPickerRef.current?.();

  const handleDiscard = () => {
    // Discard the freshly-picked image only; revert to the prior state
    // (the existing saved avatar, or the empty placeholder).
    handleRefreshMediaItem(undefined);
    onStagedMediaChange?.(null);
  };

  return (
    <View>
      <CropCircleImage
        size={size}
        borderRadius={8}
        source={displaySource ? { uri: displaySource } : undefined}
        progress={staged?.uploadProgress}
        uploading={isUploading}
        showClose={!!staged && !isUploading}
        onClose={handleDiscard}
        onPress={isUploading ? undefined : openPicker}
        testID="profile-avatar-upload"
      />
      <ImagePicker
        mode={PickerMode.CAMERA}
        IsSquareCameraView
        acceptedMediaTypes={[MediaType.Image]}
        setMedia={(media: ImageData) => handleSet(media)}
        setProgress={handleUploadProgress}
        setUploading={(uploading: boolean) => onUploadingChange?.(uploading)}
        onPickerReady={(open: () => void) => {
          openPickerRef.current = open;
        }}
        buttonComponent={<View style={styles.hiddenTrigger} />}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  hiddenTrigger: {
    width: 0,
    height: 0,
    overflow: 'hidden',
  },
});
